// Info button and modal overlay.
// mountWhaleOverlay — adds the Info button; returns a cleanup function.
// showInfoModal      — adds the modal backdrop over the button; backdrop/button
//                      click both dismiss with a fade-out.

import { appConfig } from '../../config/appConfig';
import { openInfoModal, closeInfoModal } from '../../state/appState';

/**
 * Mounts the persistent Info button into `slot`.
 * Call cleanup to remove it (e.g. on state exit).
 *
 * Returns a cleanup function.
 */
export function mountWhaleOverlay(slot: HTMLElement): () => void {
  const btn = document.createElement('button');
  btn.className = 'ar-info-btn';
  btn.textContent = appConfig.copy.infoButtonLabel;
  btn.addEventListener('click', openInfoModal);
  slot.appendChild(btn);

  return () => {
    btn.removeEventListener('click', openInfoModal);
    btn.remove();
  };
}

/**
 * Adds the modal backdrop + card into `slot`.
 * The Info button (added by mountWhaleOverlay) must already be present.
 * Clicking the backdrop or the Info button dismisses the modal.
 */
export function showInfoModal(slot: HTMLElement): void {
  // Prevent duplicate modals.
  if (slot.querySelector('.ar-modal-backdrop')) return;

  const backdrop = document.createElement('div');
  backdrop.className = 'ar-modal-backdrop';

  const card = document.createElement('div');
  card.className = 'ar-modal';
  card.innerHTML = `
    <h2 class="ar-modal__title">${appConfig.copy.infoModalTitle}</h2>
    <div class="ar-modal__body">
      <p>${appConfig.copy.infoModalBody}</p>
    </div>
  `;

  // Stop clicks inside the card from reaching the backdrop.
  card.addEventListener('click', (e) => e.stopPropagation());

  backdrop.appendChild(card);
  slot.appendChild(backdrop);

  const btn = slot.querySelector<HTMLButtonElement>('.ar-info-btn');

  const dismiss = () => {
    backdrop.classList.add('ar-modal-backdrop--out');

    // Fallback: if animationend never fires (e.g. animations disabled, reduced
    // motion preference, or interrupted), remove the modal after 400ms.
    const fallbackTimer = window.setTimeout(finalize, 400);

    backdrop.addEventListener('animationend', () => {
      clearTimeout(fallbackTimer);
      finalize();
    }, { once: true });

    function finalize() {
      backdrop.remove();
      // Restore Info button to open the modal again.
      if (btn) {
        btn.removeEventListener('click', dismiss);
        btn.addEventListener('click', openInfoModal);
      }
      closeInfoModal();
    }
  };

  // Backdrop click closes modal.
  backdrop.addEventListener('click', dismiss);

  // Info button click while modal is open also closes it.
  if (btn) {
    btn.removeEventListener('click', openInfoModal);
    btn.addEventListener('click', dismiss);
  }
}
